import {
  $convertFromMarkdownString,
  ELEMENT_TRANSFORMERS,
  ElementTransformer,
  MULTILINE_ELEMENT_TRANSFORMERS,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  TextMatchTransformer,
  Transformer,
  TRANSFORMERS,
} from "@lexical/markdown";
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isLineBreakNode,
  $isParagraphNode,
  $isTextNode,
  ElementNode,
  IS_UNDERLINE,
  LexicalNode,
  ParagraphNode,
  TextNode,
} from "lexical";
import {
  EMPTY_PARAGRAPH_HTML,
  EMPTY_PARAGRAPH_MARKER,
  HANDLEBARS_BLOCK_TAG_REG_EXP,
  SEPARATOR_ROW_REG_EXP,
} from "src/components/DocViewer/components/markdownDocViewer/rawEditor/preprocessMarkdown";
import { $createHtmlNode, $isHtmlNode, HtmlNode } from "../HtmlNode";
import { $createImageNode, $isImageNode, ImageNode } from "../ImageNode";

const HANDLEBARS_BLOCK_TAG_LINE_REG_EXP = new RegExp(
  `^\\s*${HANDLEBARS_BLOCK_TAG_REG_EXP.source}\\s*$`
);

/**
 * True if `text` contains a Handlebars block-control tag (`{{#if}}`,
 * `{{/if}}`, etc.). Used to decide soft-break export style next to
 * Handlebars syntax. Does NOT match plain `{{ var }}` interpolations.
 */
const containsHandlebarsBlockTag = (text: string): boolean =>
  HANDLEBARS_BLOCK_TAG_REG_EXP.test(text);

// Very primitive table setup - trailing | is optional to support AI-generated tables
const TABLE_ROW_REG_EXP = /^\|(.+?)\|?\s*$/;

/**
 * Serializes a single paragraph's inline content:
 * - LineBreakNodes → <br />
 * - TextNodes with formatting → **bold**, *italic*, <u>underline</u>
 * - ImageNodes → Markdown, or HTML when a width or class is present
 * - Empty paragraphs → <p><br /></p>
 *
 * This is used by both the PARAGRAPH transformer (for root-level paragraphs)
 * and $serializeCellContent (for table cells).
 */
export const $serializeParagraphContent = (
  paragraph: ParagraphNode | ElementNode
): string => {
  const children = paragraph.getChildren();

  // Check if paragraph is truly empty (no children or only empty text)
  const hasContent = children.some((child) => {
    if ($isImageNode(child)) return true;
    if ($isLineBreakNode(child)) return true;
    const text = child.getTextContent().replace(/\u200B/g, "");
    return text.length > 0;
  });

  if (!hasContent) {
    return EMPTY_PARAGRAPH_HTML;
  }

  // Non-empty paragraph - serialize children
  const parts: string[] = [];
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if ($isLineBreakNode(child)) {
      // If a sibling of this soft break carries a Handlebars block tag in
      // its text, emit `\n` instead of `<br />` so the v1 newline-separated
      // shape round-trips. Plain prose paragraphs keep the existing
      // `<br />` behavior.
      const prevText = children[i - 1]?.getTextContent() ?? "";
      const nextText = children[i + 1]?.getTextContent() ?? "";
      const adjacentToHandlebarsBlock =
        containsHandlebarsBlockTag(prevText) ||
        containsHandlebarsBlockTag(nextText);
      parts.push(adjacentToHandlebarsBlock ? "\n" : "<br />");
    } else if ($isImageNode(child)) {
      // Write layout and diff metadata as standard HTML attributes.
      const altText = child.getAltText();
      const src = child.getSrc();
      const width = child.getWidth();
      const className = child.__className;
      if (width || className) {
        parts.push(
          `<img src="${src}" alt="${altText}"${width ? ` width="${width}"` : ""}${className ? ` class="${className}"` : ""} />`
        );
      } else {
        parts.push(`![${altText}](${src})`);
      }
    } else if ($isTextNode(child)) {
      // Strip ZWSP characters from text content
      let text = child.getTextContent().replace(/\u200B/g, "");
      if (!text) continue; // Skip empty text nodes
      const format = child.getFormat();
      if (format & 1) text = `**${text}**`; // bold
      if (format & 2) text = `*${text}*`; // italic
      if (format & IS_UNDERLINE) text = `<u>${text}</u>`; // underline
      parts.push(text);
    } else {
      // Strip ZWSP from other node types too
      const content = child.getTextContent().replace(/\u200B/g, "");
      if (content) parts.push(content);
    }
  }
  return parts.join("");
};

/**
 * Serializes table cell content:
 * - LineBreakNodes → <br />
 * - Paragraph boundaries → <br /> (cells normally hold a single paragraph;
 *   extra paragraphs only appear in legacy content)
 * - Empty paragraphs → collapse to an extra <br />
 */
const $serializeCellContent = (cell: TableCellNode): string => {
  const paragraphs = cell.getChildren();
  const serializedParagraphs: string[] = [];

  for (const paragraph of paragraphs) {
    if (!$isParagraphNode(paragraph)) continue;
    const content = $serializeParagraphContent(paragraph);
    serializedParagraphs.push(content === EMPTY_PARAGRAPH_HTML ? "" : content);
  }

  const hasNonEmptyParagraph = serializedParagraphs.some(
    (content) => content !== ""
  );
  if (!hasNonEmptyParagraph) {
    return "";
  }

  return serializedParagraphs.join("<br />");
};

/**
 * If `row`'s first cell text is a bare Handlebars block-control tag and the
 * remaining cells are empty, return the tag text. Otherwise return null.
 *
 * This detects rows synthesized at import time by `wrapHandlebarsTableBlocks`,
 * which encodes block-tag lines that originally sat between table rows as
 * ordinary one-tag rows. The handlebars syntax itself is the marker — no
 * sentinel prefix is used.
 */
const $extractHandlebarsSentinelRow = (row: LexicalNode): string | null => {
  if (!$isTableRowNode(row)) return null;
  const cells = row.getChildren();
  if (cells.length === 0) return null;
  const first = cells[0];
  if (!$isTableCellNode(first)) return null;
  const firstText = first.getTextContent().trim();
  if (!HANDLEBARS_BLOCK_TAG_LINE_REG_EXP.test(firstText)) return null;
  for (let i = 1; i < cells.length; i++) {
    const cell = cells[i];
    if (!$isTableCellNode(cell)) continue;
    if (cell.getTextContent().trim() !== "") return null;
  }
  return firstText;
};

export const TABLE: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node: LexicalNode) => {
    if (!$isTableNode(node)) {
      return null;
    }

    const output: string[] = [];
    const rows = node.getChildren();

    if (rows.length === 0) {
      // Empty table - return basic structure
      return "| | |\n| --- | --- |";
    }

    for (const row of rows) {
      const rowOutput = [];
      if (!$isTableRowNode(row)) {
        continue;
      }

      // Handlebars sentinel row: a row whose only meaningful content is a
      // single block-kind HandlebarsTagNode (rest of the cells are empty).
      // These were synthesized at import time from `{{#if}}`/`{{/if}}` lines
      // wedged between table rows. Emit them as the bare `{{...}}` line so
      // the v1 source shape is preserved.
      const handlebarsSentinelRaw = $extractHandlebarsSentinelRow(row);
      if (handlebarsSentinelRaw !== null) {
        output.push(handlebarsSentinelRaw);
        continue;
      }

      let isHeaderRow = false;
      const cells = row.getChildren();

      for (const cell of cells) {
        if ($isTableCellNode(cell)) {
          let cellContent = $serializeCellContent(cell);

          // Escape pipe characters
          cellContent = cellContent.replace(/\|/g, "\\|");

          rowOutput.push(cellContent || " ");

          if (cell.__headerState === TableCellHeaderStates.ROW) {
            isHeaderRow = true;
          }
        }
      }

      // Ensure we have at least one cell
      if (rowOutput.length === 0) {
        rowOutput.push(" ");
      }

      output.push(`| ${rowOutput.join(" | ")} |`);
      if (isHeaderRow) {
        output.push(`| ${rowOutput.map((_) => "---").join(" | ")} |`);
      }
    }

    return output.join("\n");
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _1, match) => {
    // Header row
    if (SEPARATOR_ROW_REG_EXP.test(match[0])) {
      const table = parentNode.getPreviousSibling();
      if (!table || !$isTableNode(table)) {
        return;
      }

      const rows = table.getChildren();
      const lastRow = rows[rows.length - 1];
      if (!lastRow || !$isTableRowNode(lastRow)) {
        return;
      }

      // Add header state to row cells
      lastRow.getChildren().forEach((cell) => {
        if (!$isTableCellNode(cell)) {
          return;
        }
        cell.setHeaderStyles(
          TableCellHeaderStates.ROW,
          TableCellHeaderStates.ROW
        );
      });

      // Remove line
      parentNode.remove();
      return;
    }

    const matchCells = mapToTableCells(match[0]);

    if (matchCells == null) {
      return;
    }

    const rows = [matchCells];
    let sibling = parentNode.getPreviousSibling();
    let maxCells = matchCells.length;

    while (sibling) {
      if (!$isParagraphNode(sibling)) {
        break;
      }

      // Get text content from paragraph (handles formatted text like bold)
      const paragraphText = (sibling as ElementNode).getTextContent();
      const cells = mapToTableCells(paragraphText);

      if (cells == null) {
        break;
      }

      maxCells = Math.max(maxCells, cells.length);
      rows.unshift(cells);
      const previousSibling = sibling.getPreviousSibling();
      sibling.remove();
      sibling = previousSibling;
    }

    // Look forwards for table rows (to catch rows that come after)
    let nextSibling = parentNode.getNextSibling();
    let foundSeparator = false;
    while (nextSibling) {
      if (!$isParagraphNode(nextSibling)) {
        break;
      }

      // Get text content from paragraph (handles formatted text like bold)
      // Trim to remove any leading/trailing whitespace that might break table recognition
      const paragraphText = (nextSibling as ElementNode)
        .getTextContent()
        .trim();

      // Handle separator row - mark first row as header and remove the separator
      if (SEPARATOR_ROW_REG_EXP.test(paragraphText)) {
        foundSeparator = true;
        const followingSibling = nextSibling.getNextSibling();
        nextSibling.remove();
        nextSibling = followingSibling;
        continue;
      }

      const cells = mapToTableCells(paragraphText);

      if (cells == null) {
        break;
      }

      maxCells = Math.max(maxCells, cells.length);
      rows.push(cells);
      const followingSibling = nextSibling.getNextSibling();
      nextSibling.remove();
      nextSibling = followingSibling;
    }

    const table = $createTableNode();

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const cells = rows[rowIndex];
      const tableRow = $createTableRowNode();
      table.append(tableRow);
      const isHeaderRow = foundSeparator && rowIndex === 0;

      for (let i = 0; i < maxCells; i++) {
        const cell = i < cells.length ? cells[i] : $createTableCell("");
        // If this is the header row (separator was found), set header styles
        if (isHeaderRow && $isTableCellNode(cell)) {
          cell.setHeaderStyles(
            TableCellHeaderStates.ROW,
            TableCellHeaderStates.ROW
          );
        }
        tableRow.append(cell);
      }
    }

    const previousSibling = parentNode.getPreviousSibling();
    if (
      $isTableNode(previousSibling) &&
      getTableColumnsSize(previousSibling) === maxCells
    ) {
      previousSibling.append(...table.getChildren());
      parentNode.remove();
    } else {
      parentNode.replace(table);
    }

    table.selectEnd();
  },
  type: "element",
};

function getTableColumnsSize(table: TableNode) {
  const row = table.getFirstChild();
  return $isTableRowNode(row) ? row.getChildrenSize() : 0;
}

/**
 * PARAGRAPH transformer for root-level paragraphs.
 * Exports LineBreakNodes as <br /> and empty paragraphs as <p><br /></p>.
 * Preserves indentation as leading spaces for list continuations.
 * This ensures consistent serialization between table cells and root content.
 */
export const PARAGRAPH: ElementTransformer = {
  dependencies: [ParagraphNode],
  export: (
    node: LexicalNode,
    _traverseChildren: (node: ElementNode) => string
  ) => {
    if (!$isParagraphNode(node)) {
      return null;
    }
    const content = $serializeParagraphContent(node);

    // Preserve indentation as leading spaces (4 spaces per indent level)
    const indent = node.getIndent();
    if (indent > 0) {
      const spaces = "    ".repeat(indent);
      return spaces + content;
    }

    return content;
  },
  // Use a regex that never matches - paragraphs are the default fallback during import
  // and don't need explicit pattern matching
  regExp: /(?!)/,
  replace: (
    _parentNode: ElementNode,
    _children: Array<LexicalNode>,
    _match: Array<string>,
    _isImport: boolean
  ) => {
    // No-op - paragraph import is handled by Lexical's default behavior
    // Return false to indicate this transformer didn't handle the import
    return false;
  },
  type: "element",
};

/**
 * Parses markdown text formatting and creates TextNodes with appropriate format.
 * Handles:
 * - <u>text</u> → underline
 * - ***text*** or ___text___ → bold + italic
 * - **text** or __text__ → bold
 * - *text* or _text_ → italic
 *
 * @param text - The text content potentially containing markdown formatting
 * @returns Array of TextNodes with appropriate formatting applied
 */
const $parseMarkdownFormatting = (text: string): TextNode[] => {
  const nodes: TextNode[] = [];

  // Regex to match markdown formatting:
  // - <u>text</u> (underline)
  // - ***text*** or ___text___ (bold + italic) - must be matched first
  // - **text** or __text__ (bold)
  // - *text* or _text_ (italic)
  // Using non-greedy matching and capturing groups
  const formatRegex =
    /<u>(.+?)<\/u>|(\*\*\*|___)(.+?)\2|(\*\*|__)(.+?)\4|(\*|_)(.+?)\6/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = formatRegex.exec(text)) !== null) {
    // Add plain text before this match
    if (match.index > lastIndex) {
      const plainText = text.substring(lastIndex, match.index);
      if (plainText) {
        nodes.push($createTextNode(plainText));
      }
    }

    // Determine which group matched and create the formatted node
    let formattedText: string;
    let format: number;

    if (match[1]) {
      // Underline (<u>text</u>) - parse inner markdown and apply underline
      const underlineNodes = $parseMarkdownFormatting(match[1]);
      for (const node of underlineNodes) {
        if ($isTextNode(node)) {
          node.setFormat(node.getFormat() | IS_UNDERLINE);
        }
        nodes.push(node);
      }
    } else if (match[2]) {
      // Bold + italic (*** or ___)
      formattedText = match[3];
      format = 3; // 1 (bold) | 2 (italic) = 3
      const textNode = $createTextNode(formattedText);
      textNode.setFormat(format);
      nodes.push(textNode);
    } else if (match[4]) {
      // Bold (** or __)
      formattedText = match[5];
      format = 1; // bold
      const textNode = $createTextNode(formattedText);
      textNode.setFormat(format);
      nodes.push(textNode);
    } else {
      // Italic (* or _)
      formattedText = match[7];
      format = 2; // italic
      const textNode = $createTextNode(formattedText);
      textNode.setFormat(format);
      nodes.push(textNode);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining plain text after the last match
  if (lastIndex < text.length) {
    const plainText = text.substring(lastIndex);
    if (plainText) {
      nodes.push($createTextNode(plainText));
    }
  }

  // If no formatting was found, return a single plain text node
  if (nodes.length === 0 && text) {
    nodes.push($createTextNode(text));
  }

  return nodes;
};

/**
 * Parses inline content and creates nodes for images and formatted text.
 * Images are parsed first, then text segments get markdown formatting.
 */
const $parseInlineContent = (content: string): LexicalNode[] => {
  const nodes: LexicalNode[] = [];
  const imageRegex =
    /!\[([^\]]*)\]\(([^)]+)\)(?:\{width:(\d+)\})?(?:\{class:"([^"]+)"\})?/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = imageRegex.exec(content)) !== null) {
    const [fullMatch, altText, src, widthStr, className] = match;
    const before = content.substring(lastIndex, match.index);
    if (before) {
      nodes.push(...$parseMarkdownFormatting(before));
    }
    const width = widthStr ? parseInt(widthStr, 10) : undefined;
    nodes.push($createImageNode(src, altText || "", width, className));
    lastIndex = match.index + fullMatch.length;
  }

  const after = content.substring(lastIndex);
  if (after) {
    nodes.push(...$parseMarkdownFormatting(after));
  }

  return nodes;
};

/**
 * Creates a paragraph node from text content, handling:
 * - <br /> → LineBreakNode
 * - **text** → bold
 * - *text* → italic
 * - <u>text</u> → underline
 */
const $createParagraphFromContent = (content: string): ElementNode => {
  const paragraph = $createParagraphNode();

  // Empty paragraph
  if (!content || content === EMPTY_PARAGRAPH_MARKER) {
    return paragraph;
  }

  // Split by <br /> to handle line breaks
  const parts = content.split(/<br\s*\/?>/i);

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    if (part) {
      const inlineNodes = $parseInlineContent(part);
      for (const node of inlineNodes) {
        paragraph.append(node);
      }
    }

    // Add line break between parts (not after the last one)
    if (i < parts.length - 1) {
      paragraph.append($createLineBreakNode());
    }
  }

  return paragraph;
};

const $createTableCell = (textContent: string): TableCellNode => {
  const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);

  // Unescape pipe characters
  textContent = textContent.replace(/\\\|/g, "|");

  // Legacy content: empty paragraph HTML collapses to nothing (the
  // surrounding <br /> separators already contribute line breaks)
  textContent = textContent.replace(
    /<p\s*>(?:\s*<br\s*\/?\s*>\s*)?<\/p>|<p\s*\/\s*>/gi,
    ""
  );

  cell.append($createParagraphFromContent(textContent.trim()));

  return cell;
};

export const mapToTableCells = (
  textContent: string
): Array<TableCellNode> | null => {
  // Trim the text content to remove any leading/trailing whitespace
  const trimmedText = textContent.trim();
  const match = trimmedText.match(TABLE_ROW_REG_EXP);
  if (!match || !match[1]) {
    return null;
  }
  // Split the captured content by | to get individual cells
  // Don't trim the whole content first - we need to preserve empty cells
  // The captured group is the content between the first | and the optional trailing |
  let content = match[1];
  // Remove trailing | if present (the regex makes it optional)
  if (content.endsWith("|")) {
    content = content.slice(0, -1);
  }
  // Split by | and trim each cell text before creating the cell
  // This preserves empty cells (which become empty strings after trim)
  const cells = content.split("|").map((text) => $createTableCell(text.trim()));
  return cells;
};

// Image transformer for Markdown syntax, or HTML when an image has layout or diff metadata.
// Note: The export function here is NOT used for paragraphs - $serializeParagraphContent handles images directly.
// This export is kept for completeness in case ImageNode appears in other contexts.
export const IMAGE: TextMatchTransformer = {
  dependencies: [ImageNode],
  export: (node: LexicalNode) => {
    if (!$isImageNode(node)) {
      return null;
    }
    const altText = node.getAltText();
    const src = node.getSrc();
    const width = node.getWidth();
    if (width || node.__className) {
      return `<img src="${src}" alt="${altText}"${width ? ` width="${width}"` : ""}${node.__className ? ` class="${node.__className}"` : ""} />`;
    }
    return `![${altText}](${src})`;
  },
  // Continue accepting the legacy suffix syntax when reading historical content.
  importRegExp:
    /!\[([^\]]*)\]\(([^)]+)\)(?:\{width:(\d+)\})?(?:\{class:"([^"]+)"\})?/,
  regExp:
    /!\[([^\]]*)\]\(([^)]+)\)(?:\{width:(\d+)\})?(?:\{class:"([^"]+)"\})?$/,
  replace: (textNode: TextNode, match: RegExpMatchArray) => {
    const [, altText, src, widthStr, className] = match;
    const width = widthStr ? parseInt(widthStr, 10) : undefined;
    textNode.replace($createImageNode(src, altText || "", width, className));
  },
  trigger: ")",
  type: "text-match",
};

const createUnderlinedNodes = (content: string): LexicalNode[] => {
  const nodes = $parseMarkdownFormatting(content);
  for (const node of nodes) {
    if ($isTextNode(node)) {
      node.setFormat(node.getFormat() | IS_UNDERLINE);
    }
  }
  return nodes;
};

const replaceTextNodeWithNodes = (textNode: TextNode, nodes: LexicalNode[]) => {
  if (nodes.length === 0) {
    return;
  }

  let current = nodes[0];
  textNode.replace(current);

  for (let i = 1; i < nodes.length; i += 1) {
    current.insertAfter(nodes[i]);
    current = nodes[i];
  }
};

export const UNDERLINE: TextMatchTransformer = {
  dependencies: [TextNode],
  export: () => null,
  importRegExp: /<u>(.+?)<\/u>/,
  regExp: /<u>(.+?)<\/u>$/,
  replace: (textNode: TextNode, match: RegExpMatchArray) => {
    const underlineContent = match[1];
    if (!underlineContent) {
      return;
    }

    const nodes = createUnderlinedNodes(underlineContent);
    replaceTextNodeWithNodes(textNode, nodes);
  },
  trigger: "}",
  type: "text-match",
};

/**
 * Parses HTML from a string, handling nested tags correctly.
 * Returns an array of matches with their positions and full HTML content.
 */
function parseHtmlFromText(
  text: string
): Array<{ index: number; html: string }> {
  const matches: Array<{ index: number; html: string }> = [];
  let i = 0;

  while (i < text.length) {
    // Find the next opening tag
    const openTagStart = text.indexOf("<", i);
    if (openTagStart === -1) break;

    // Check if it's a closing tag (</tag>)
    if (text[openTagStart + 1] === "/") {
      i = openTagStart + 1;
      continue;
    }

    // Find the end of the opening tag
    const openTagEnd = text.indexOf(">", openTagStart);
    if (openTagEnd === -1) break;

    const openTag = text.substring(openTagStart, openTagEnd + 1);

    // Extract tag name
    const tagNameMatch = openTag.match(/<([a-zA-Z][a-zA-Z0-9]*)/);
    if (!tagNameMatch) {
      i = openTagEnd + 1;
      continue;
    }

    const tagName = tagNameMatch[1].toLowerCase();
    const isSelfClosing =
      openTag.endsWith("/>") ||
      openTag.match(/\/\s*>/) ||
      // Void elements that don't need closing tags
      [
        "img",
        "br",
        "hr",
        "input",
        "meta",
        "link",
        "area",
        "base",
        "col",
        "embed",
        "source",
        "track",
        "wbr",
      ].includes(tagName);

    if (isSelfClosing) {
      // Self-closing tag - add it as a match
      matches.push({
        index: openTagStart,
        html: openTag,
      });
      i = openTagEnd + 1;
      continue;
    }

    // Find the matching closing tag
    let depth = 1;
    let searchIndex = openTagEnd + 1;
    let closingTagIndex = -1;

    while (searchIndex < text.length && depth > 0) {
      // Find next opening tag - must be <tagName followed by space, >, or /, but NOT </tagName
      let nextOpen = -1;
      let searchPos = searchIndex;
      while (searchPos < text.length) {
        const potentialOpen = text.indexOf(`<${tagName}`, searchPos);
        if (potentialOpen === -1) break;

        // Check if this is actually an opening tag (not a closing tag)
        // After <tagName, we should have: space, >, or /, but NOT </
        const afterTagName = potentialOpen + tagName.length + 1;
        if (afterTagName < text.length) {
          const char = text[afterTagName];
          // If it's a closing tag, the char would be '/' (like </div>)
          // If it's an opening tag, the char would be space, >, or part of an attribute
          if (char !== "/") {
            // This looks like an opening tag
            nextOpen = potentialOpen;
            break;
          }
        }
        searchPos = potentialOpen + 1;
      }

      const nextClose = text.indexOf(`</${tagName}>`, searchIndex);

      if (nextClose === -1) {
        // No closing tag found - treat as self-closing
        break;
      }

      if (nextOpen !== -1 && nextOpen < nextClose) {
        // Found another opening tag before the closing tag
        depth++;
        // Find the end of this opening tag
        const tagEnd = text.indexOf(">", nextOpen);
        if (tagEnd !== -1) {
          searchIndex = tagEnd + 1;
        } else {
          searchIndex = nextOpen + 1;
        }
      } else {
        // Found a closing tag
        depth--;
        if (depth === 0) {
          closingTagIndex = nextClose;
          break;
        }
        searchIndex = nextClose + `</${tagName}>`.length;
      }
    }

    if (closingTagIndex !== -1) {
      // Found matching closing tag
      const closingTag = `</${tagName}>`;
      const fullHtml = text.substring(
        openTagStart,
        closingTagIndex + closingTag.length
      );
      matches.push({
        index: openTagStart,
        html: fullHtml,
      });
      i = closingTagIndex + closingTag.length;
    } else {
      // No closing tag found - treat as self-closing
      matches.push({
        index: openTagStart,
        html: openTag,
      });
      i = openTagEnd + 1;
    }
  }

  return matches;
}

/**
 * Processes HTML content in a container node (like a table cell) by getting all text content,
 * processing HTML, and replacing the container's children.
 */
function processHtmlInContainer(container: ElementNode): boolean {
  // Get all text content from the container
  const fullText = container.getTextContent();

  // Check if container contains HTML
  if (!/<[a-zA-Z][a-zA-Z0-9]*(?:\s+[^>]*)?(?:\/>|>)/.test(fullText)) {
    return false;
  }

  const matches = parseHtmlFromText(fullText);
  if (matches.length === 0) {
    return false;
  }

  // Build new nodes from the processed HTML
  let lastIndex = 0;
  const nodesToInsert: LexicalNode[] = [];

  for (const htmlMatch of matches) {
    // Add text before HTML if any
    if (htmlMatch.index > lastIndex) {
      const textBefore = fullText.substring(lastIndex, htmlMatch.index);
      if (textBefore.trim() || textBefore) {
        // Convert text to markdown nodes using a temporary paragraph
        const tempParagraph = $createParagraphNode();
        $convertFromMarkdownString(
          textBefore,
          PLAYGROUND_TRANSFORMERS,
          tempParagraph
        );
        const children = tempParagraph.getChildren();
        for (const child of children) {
          nodesToInsert.push(child);
        }
      }
    }

    // Add HTML node
    nodesToInsert.push($createHtmlNode(htmlMatch.html));

    lastIndex = htmlMatch.index + htmlMatch.html.length;
  }

  // Add remaining text if any
  if (lastIndex < fullText.length) {
    const textAfter = fullText.substring(lastIndex);
    if (textAfter.trim() || textAfter) {
      // Convert text to markdown nodes using a temporary paragraph
      const tempParagraph = $createParagraphNode();
      $convertFromMarkdownString(
        textAfter,
        PLAYGROUND_TRANSFORMERS,
        tempParagraph
      );
      const children = tempParagraph.getChildren();
      for (const child of children) {
        nodesToInsert.push(child);
      }
    }
  }

  // Replace all children of the container
  if (nodesToInsert.length > 0) {
    const childrenCount = container.getChildrenSize();
    // Remove all existing children
    for (let i = childrenCount - 1; i >= 0; i--) {
      const child = container.getChildAtIndex(i);
      if (child) {
        child.remove();
      }
    }
    // Add new children
    for (const node of nodesToInsert) {
      container.append(node);
    }
    return true;
  }

  return false;
}

/**
 * Processes HTML content in text nodes and converts them to HtmlNode instances.
 * This function walks through the Lexical node tree and converts text nodes containing HTML
 * to HtmlNode decorator nodes that can render the HTML.
 *
 * @param root - The root node to process
 */
export function $processHtmlInNodes(root: ReturnType<typeof $getRoot>): void {
  // Iterate multiple times to catch HTML in newly created text nodes
  // This handles cases where HTML processing creates new text nodes that also contain HTML
  let processedAny = true;
  let iterations = 0;
  const maxIterations = 10; // Prevent infinite loops

  while (processedAny && iterations < maxIterations) {
    processedAny = false;
    iterations++;

    // First, process table cells as containers (HTML can span multiple text nodes in cells)
    const tableCellsToProcess: TableCellNode[] = [];

    const collectTableCells = (node: LexicalNode): void => {
      if ($isTableCellNode(node)) {
        const textContent = node.getTextContent();
        // Check if this cell contains HTML
        if (/<[a-zA-Z][a-zA-Z0-9]*(?:\s+[^>]*)?(?:\/>|>)/.test(textContent)) {
          tableCellsToProcess.push(node);
        }
      } else if (node instanceof ElementNode) {
        const children = node.getChildren();
        for (const child of children) {
          collectTableCells(child);
        }
      }
    };

    // Collect all table cells that contain HTML
    const children = root.getChildren();
    for (const child of children) {
      collectTableCells(child);
    }

    // Process table cells first
    for (const cell of tableCellsToProcess) {
      if (cell.isAttached()) {
        if (processHtmlInContainer(cell)) {
          processedAny = true;
        }
      }
    }

    // Then process other text nodes (for HTML outside of table cells)
    const textNodesToProcess: TextNode[] = [];

    const collectTextNodes = (node: LexicalNode): void => {
      // Skip table cells as we already processed them
      if ($isTableCellNode(node)) {
        return;
      }

      if ($isParagraphNode(node)) {
        const paragraphText = (node as ElementNode).getTextContent().trim();

        // Check if this paragraph matches table row pattern
        if (TABLE_ROW_REG_EXP.test(paragraphText)) {
          return;
        }

        // Also check if paragraph starts and ends with | (might be a table row)
        // Even if HTML has been partially processed, we should skip it
        if (paragraphText.startsWith("|") && paragraphText.includes("|", 1)) {
          const cellCount = (paragraphText.match(/\|/g) || []).length;
          if (cellCount >= 2) {
            return;
          }
        }
      }

      if ($isTextNode(node)) {
        const textContent = node.getTextContent();
        // Check if this text node contains HTML
        if (/<[a-zA-Z][a-zA-Z0-9]*(?:\s+[^>]*)?(?:\/>|>)/.test(textContent)) {
          textNodesToProcess.push(node);
        }
      } else if (node instanceof ElementNode) {
        // Process children recursively - only ElementNodes have getChildren()
        const children = node.getChildren();
        for (const child of children) {
          collectTextNodes(child);
        }
      }
    };

    // Collect all text nodes that contain HTML (excluding those in table cells)
    for (const child of children) {
      collectTextNodes(child);
    }

    // Process each text node
    for (const textNode of textNodesToProcess) {
      // Check if node is still attached (might have been removed in previous iteration)
      if (!textNode.isAttached()) {
        continue;
      }

      const textContent = textNode.getTextContent();
      const matches = parseHtmlFromText(textContent);

      // If we found HTML, process it
      if (matches.length > 0) {
        let lastIndex = 0;
        const nodesToInsert: LexicalNode[] = [];

        for (const htmlMatch of matches) {
          // Add text before HTML if any
          if (htmlMatch.index > lastIndex) {
            const textBefore = textContent.substring(
              lastIndex,
              htmlMatch.index
            );
            if (textBefore) {
              nodesToInsert.push($createTextNode(textBefore));
            }
          }

          // Add HTML node
          nodesToInsert.push($createHtmlNode(htmlMatch.html));

          lastIndex = htmlMatch.index + htmlMatch.html.length;
        }

        // Add remaining text if any
        if (lastIndex < textContent.length) {
          const textAfter = textContent.substring(lastIndex);
          if (textAfter) {
            nodesToInsert.push($createTextNode(textAfter));
          }
        }

        // Replace the text node with the processed nodes
        if (nodesToInsert.length > 0) {
          const parent = textNode.getParent();
          if (parent && textNode.isAttached()) {
            const firstNode = nodesToInsert[0];
            textNode.replace(firstNode);
            // Insert remaining nodes after the first
            let previousNode = firstNode;
            for (let i = 1; i < nodesToInsert.length; i++) {
              previousNode.insertAfter(nodesToInsert[i]);
              previousNode = nodesToInsert[i];
            }
            processedAny = true;
          }
        }
      }
    }
  }
}

/**
 * HTML transformer that converts HTML strings in markdown to HtmlNode instances.
 * This transformer processes text to find HTML patterns and replaces them with HtmlNode decorators.
 *
 * It handles:
 * - Self-closing tags: <br />, <img ... />
 * - Paired tags: <div>...</div>, <span>...</span>
 * - Tags with attributes and nested content
 */
export const HTML: TextMatchTransformer = {
  dependencies: [HtmlNode],
  export: (node: LexicalNode) => {
    if (!$isHtmlNode(node)) {
      return null;
    }
    return node.getHtml();
  },
  // Match HTML tags - handles both self-closing tags and opening tags
  // This will match patterns like: <div style="...">, <img ... />, <br />
  importRegExp: /<[a-zA-Z][a-zA-Z0-9]*(?:\s+[^>]*)?(?:\/>|>)/,
  regExp: /<[a-zA-Z][a-zA-Z0-9]*(?:\s+[^>]*)?(?:\/>|>)/,
  replace: (textNode: TextNode, match: RegExpMatchArray) => {
    const htmlStart = match[0];
    const tagNameMatch = htmlStart.match(/<([a-zA-Z][a-zA-Z0-9]*)/);
    if (!tagNameMatch) {
      return;
    }

    const tagName = tagNameMatch[1].toLowerCase();
    const isSelfClosing = htmlStart.endsWith("/>") || htmlStart.endsWith("/ >");

    // For self-closing tags, create the HTML node immediately
    if (isSelfClosing) {
      const htmlNode = $createHtmlNode(htmlStart);
      textNode.replace(htmlNode);
      return;
    }

    // For paired tags, we need to find the closing tag
    // This is a simplified approach - for complex nested HTML, a more sophisticated parser would be needed
    const textAfter = textNode
      .getTextContent()
      .substring(match.index! + match[0].length);
    const closingTag = `</${tagName}>`;
    const closingIndex = textAfter.indexOf(closingTag);

    if (closingIndex !== -1) {
      // Found closing tag - extract the full HTML including content
      const fullHtml = textNode
        .getTextContent()
        .substring(
          match.index!,
          match.index! + match[0].length + closingIndex + closingTag.length
        );
      const htmlNode = $createHtmlNode(fullHtml);
      textNode.replace(htmlNode);
    } else {
      // No closing tag found - treat as self-closing or malformed
      // Create node with just the opening tag
      const htmlNode = $createHtmlNode(htmlStart);
      textNode.replace(htmlNode);
    }
  },
  trigger: ">",
  type: "text-match",
};

export const PLAYGROUND_TRANSFORMERS: Transformer[] = [
  TABLE,
  PARAGRAPH,
  IMAGE,
  UNDERLINE,
  ...ELEMENT_TRANSFORMERS,
  ...MULTILINE_ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
  ...TRANSFORMERS,
];
